import { JSX, useState } from "react";
import { Col, ColorPicker, Divider, Form, Row, Switch, Tag, Upload } from "antd";
import { DeleteOutlined, DownOutlined, PercentageOutlined, PlusOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import styled from "styled-components";
import Text from "../../../components/Text/Text";
import Button from "../../../components/Button/Button";
import Input from "../../../components/Input/Input";
import TextArea from "../../../components/Input/TextArea";
import CardSection from "../../../components/Card/CardSection";
import ImgCrop from "../../../components/ImgCrop/ImgCrop";
import color from "../../../resource/color";
import { ShopTierCompanyConfig } from "../../ShopTier/config";
import { ConfigTierBenefit } from "../../ShopTier/mockData";
import TierCardPreview from "../TierCardPreview";
import IconUploadBox from "../IconUploadBox";
import { EditableTierBenefit, TierBenefitItem } from "../types";
import { createImageMaxSizeValidator } from "../imageValidation";
import { icon } from "../../../resource";
import image from "../../../resource/image";

export interface WorkflowCard {
  iconUrl?: string;
  title: string;
  description: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface RuleTopic {
  title: string;
  content: string;
}

export interface Step2Result {
  bannerUrl: string | null;
  cards: WorkflowCard[];
  faqs: FaqItem[];
  rules: RuleTopic[];
  tiers: EditableTierBenefit[];
  cardImageOverrides: Record<string, string>;
  cardBackgroundOverrides: Record<string, string>;
  colorOverrides: Record<string, string>;
  changedTierKeys: string[];
}

interface Props {
  mode: "create" | "edit";
  config: ShopTierCompanyConfig;
  sourceTiers: ConfigTierBenefit[];
  startFrom?: string;
  onNext: (result: Step2Result) => void;
  onBack: () => void;
}

const DEFAULT_CARDS: WorkflowCard[] = [
  {
    title: "กดรับสินค้า = ยอดสะสมเพิ่มทันที",
    description: "ทุกครั้งที่คุณกดรับสินค้าในแอป ระบบจะบวกยอดเข้าสะสมให้อัตโนมัติ",
  },
  {
    title: "สะสมเป็นรอบรายปี (ม.ค.–ธ.ค.)",
    description: "ยอดสะสมนับเป็นรายปี และเริ่มนับใหม่ทุกวันที่ 1 มกราคม",
  },
  {
    title: "ถึงเกณฑ์ = ระดับคาดการณ์ขยับทันที",
    description:
      "พอยอดสะสมถึงเกณฑ์ ระดับคาดการณ์ของปีถัดไปจะขยับให้เห็นทันที ทางบริษัททบทวนและประกาศผลจริงในรอบทบทวนปลายปี",
  },
  {
    title: "เลื่อนขั้นแล้ว ได้อะไรเมื่อไหร่",
    description:
      "ระดับที่ประกาศตอนขึ้นปีใหม่มาพร้อมสิทธิประโยชน์เต็มของปีนั้น ยอดที่ทำปีนี้กำหนดระดับและสิทธิ์ของปีถัดไป",
  },
];

const DEFAULT_FAQS: FaqItem[] = [
  {
    question: "ระดับของฉันคำนวณจากอะไร",
    answer:
      "ระดับปีปัจจุบันคำนวณจากยอดปีก่อน (เช่น ระดับปี 2569 มาจากยอดปี 2568) ส่วนยอดที่ทำปีนี้จะกำหนดระดับของปีถัดไป",
  },
  {
    question: "ระดับจะลดกลางปีไหม",
    answer: "ไม่ลดกลางปี ระดับปรับลดเฉพาะตอนสิ้นปีเท่านั้น หากยอดทั้งปีไม่ถึงเกณฑ์ระดับเดิม",
  },
  {
    question: "ยอดแบบไหนที่นับเข้าระดับ",
    answer: "นับจากยอดที่ส่งมอบและกดรับสินค้าแล้ว (Actual Delivered) ไม่ใช่แค่ยอดสั่งซื้อ และระบบอัปเดตให้แบบเรียลไทม์",
  },
  {
    question: "สั่งถึงเกณฑ์แล้ว แต่บริษัทส่งไม่ทัน?",
    answer:
      "หากคุณสั่งซื้อถึงเกณฑ์แล้วแต่บริษัทจัดส่งไม่ทัน (สินค้าขาด) ทางบริษัทจะนำมาพิจารณาในรอบทบทวนปลายปีพร้อมระบุเหตุผล ยอดสะสมยังแสดงตามที่ส่งจริงเพื่อให้ตรงกับเอกสาร คุณจึงไม่เสียสิทธิ์จากความล่าช้าของบริษัท",
  },
  {
    question: "ยอดไม่ถึง 10 ล้านบาท เป็น Power Focus ได้ไหม",
    answer:
      "ได้ หากร้านของคุณเป็นคู่ค้ากับบริษัทในเครือและมียอดซื้อสินค้าของบริษัทในเครือถึงเกณฑ์ที่กำหนด ระบบจะนับเข้าเกณฑ์ Power Focus ให้อัตโนมัติ แม้ยอด ICP Ladda ยังไม่ถึง 10 ล้านบาท",
  },
];

const DEFAULT_RULES: RuleTopic[] = [
  {
    title: "กติกาการเข้าร่วม",
    content:
      "<p>ระบบระดับร้านค้าเปิดให้ร้านค้าที่สั่งซื้อผลิตภัณฑ์ ICP Ladda ผ่านแอป SellCoda โดยอัตโนมัติ ไม่ต้องสมัครหรือแจ้งความจำนงเพิ่มเติม ยอดซื้อสะสมจะนับจากรายการที่ร้านค้า กดรับสินค้าเรียบร้อยแล้ว ในแอปเท่านั้น ไม่ใช่ยอดที่สั่งซื้อ และจะอัปเดตให้ทันทีที่กดรับ ยอดซื้อสะสมนับเป็นรายปีปฏิทิน และรีเซ็ตเป็นศูนย์เมื่อขึ้นปีใหม่ ระดับที่ทำได้ในปีนี้จะใช้กำหนดสิทธิประโยชน์ของปีถัดไป ระดับร้านค้าจะไม่เปลี่ยนระหว่างปี ทั้งการปรับขึ้นและปรับลด ทางบริษัทพิจารณาระดับของปีถัดไปในรอบทบทวนปลายปีครั้งเดียว รวมถึงกรณีทำผิดเงื่อนไขการเข้าร่วม ซึ่งจะแจ้งเหตุผลให้ร้านค้าทราบทุกครั้ง หากยอดทั้งปีไม่ถึงเกณฑ์ของระดับเดิม</p>",
  },
  {
    title: "การเปลี่ยนแปลงและข้อยกเว้น",
    content:
      "<p>บริษัทขอสงวนสิทธิ์ในการทบทวนและปรับเกณฑ์ระดับร้านค้าในรอบทบทวนปลายปี กรณีมีเหตุสุดวิสัยที่ทำให้ยอดสะสมคลาดเคลื่อนจากความเป็นจริง เช่น ปัญหาจัดส่งของบริษัทเอง บริษัทจะพิจารณาเป็นรายกรณีและแจ้งผลให้ร้านค้าทราบ</p>",
  },
];

function tierMinThreshold(subLabel: string): number {
  const match = subLabel.match(/[\d,]+(\.\d+)?/);
  return match ? parseFloat(match[0].replace(/,/g, "")) : 0;
}

function formatVolumeAmount(value: number, volumeUnit: string): string {
  return volumeUnit === "ล้านบาท" ? value.toFixed(1) : Math.round(value).toLocaleString("th-TH");
}

const MIN_TIER_COLOR_SATURATION = 0.4;

function hexSaturation(hex: string): number {
  const clean = hex.replace("#", "");
  if (clean.length < 6) return 1;
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return max === 0 ? 0 : (max - min) / max;
}

const quillModules = {
  toolbar: [
    ["bold", "italic", "link"],
    [{ header: [false] }],
    [{ align: "" }, { align: "center" }, { align: "right" }],
    [{ list: "ordered" }, { list: "bullet" }],
  ],
};

const QuillContainer = styled.div`
  .ql-toolbar.ql-snow {
    border-radius: 4px 4px 0 0 !important;
  }
  .ql-container.ql-snow {
    border-radius: 0 0 4px 4px !important;
    font-family: Sarabun;
  }
  .ql-editor {
    min-height: 120px;
  }
  .ql-picker.ql-header .ql-picker-label::before,
  .ql-picker.ql-header .ql-picker-item::before {
    content: "ปกติ";
  }
`;

function ItemRow({ children, onDelete }: { children: React.ReactNode; onDelete: () => void }) {
  return (
    <Row wrap={false} align="middle" gutter={8} style={{ marginBottom: 8 }}>
      <Col flex="auto">{children}</Col>
      <Col flex="none">
        <div
          onClick={onDelete}
          style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            border: `1px solid ${color.error}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <DeleteOutlined style={{ color: color.error }} />
        </div>
      </Col>
    </Row>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div>
      <Text fontFamily="Sarabun" fontWeight={700} fontSize={16} color="Text2">
        {title}
      </Text>
      <div style={{ marginTop: 2 }}>
        <Text fontFamily="Sarabun" fontSize={13} color="Text3">
          {subtitle}
        </Text>
      </div>
    </div>
  );
}

function CountToggle({
  count,
  unit,
  expanded,
  onToggle,
}: {
  count: number;
  unit: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div onClick={onToggle} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
      <Text fontSize={13} color="Text3">
        {count} {unit}
      </Text>
      <DownOutlined
        style={{
          fontSize: 11,
          color: color.Text3,
          transform: expanded ? "rotate(180deg)" : "none",
          transition: "transform 0.15s ease",
        }}
      />
    </div>
  );
}

function Step2({ mode, config, sourceTiers, startFrom, onNext, onBack }: Props): JSX.Element {
  const [form] = Form.useForm();

  const [bannerUrl, setBannerUrl] = useState<string | null>(null);

  const cardsWatch: WorkflowCard[] = Form.useWatch("cards", form) || DEFAULT_CARDS;
  const faqsWatch: FaqItem[] = Form.useWatch("faqs", form) || DEFAULT_FAQS;
  const rulesWatch: RuleTopic[] = Form.useWatch("rules", form) || DEFAULT_RULES;

  const [cardsExpanded, setCardsExpanded] = useState(true);
  const [faqsExpanded, setFaqsExpanded] = useState(true);
  const [rulesExpanded, setRulesExpanded] = useState(true);
  const tierColorOf = (tierKey: string) => config.tiers.find((t) => t.key === tierKey)?.color || color.Grey;

  const buildInitialTiers = (): EditableTierBenefit[] =>
    (mode === "edit" || startFrom === "copy") && sourceTiers.length > 0
      ? sourceTiers.map((t, i) => ({
          tierKey: t.tierKey,
          thresholdLabel: t.thresholdLabel,
          tierTitle: t.tierTitle,
          titleDescription: t.titleDescription,
          percent: t.percent,
          showInApp: t.showInApp,
          benefits: t.benefits.map((b) => ({ title: b, description: b, iconUrl: icon.redeemReward })),
          queueThresholdPercent: 95,
          preventAutoDemote: i === sourceTiers.length - 1,
        }))
      : config.tiers.map((t, i) => ({
          tierKey: t.key,
          thresholdLabel: t.subLabel,
          percent: 0,
          showInApp: false,
          benefits: [],
          queueThresholdPercent: 95,
          preventAutoDemote: i === config.tiers.length - 1,
        }));

  const [tiers, setTiers] = useState<EditableTierBenefit[]>(buildInitialTiers);
  const [initialTiers] = useState<EditableTierBenefit[]>(buildInitialTiers);
  const [selectedTierKey, setSelectedTierKey] = useState<string>(config.tiers[0]?.key || "");
  const [cardImageOverrides, setCardImageOverrides] = useState<Record<string, string>>({});
  const [cardBackgroundOverrides, setCardBackgroundOverrides] = useState<Record<string, string>>({});
  const [colorOverrides, setColorOverrides] = useState<Record<string, string>>({});

  const selectedTierIndex = config.tiers.findIndex((t) => t.key === selectedTierKey);
  const selectedTierDef = config.tiers[selectedTierIndex];
  const nextTierDef = config.tiers[selectedTierIndex];
  const selectedTierBenefit = tiers.find((t) => t.tierKey === selectedTierKey);
  const originalColor = selectedTierDef?.color || color.primary;
  const selectedColor = colorOverrides[selectedTierKey] || originalColor;
  const isColorChanged = Boolean(colorOverrides[selectedTierKey]);

  const resetColor = () => {
    setColorOverrides((prev) => {
      const next = { ...prev };
      delete next[selectedTierKey];
      return next;
    });
  };

  const changedTierKeys = new Set<string>();
  tiers.forEach((t, i) => {
    if (JSON.stringify(t) !== JSON.stringify(initialTiers[i])) changedTierKeys.add(t.tierKey);
  });
  Object.keys(cardImageOverrides).forEach((k) => changedTierKeys.add(k));
  Object.keys(cardBackgroundOverrides).forEach((k) => changedTierKeys.add(k));
  Object.keys(colorOverrides).forEach((k) => changedTierKeys.add(k));
  const changedTierCount = changedTierKeys.size;

  const nextTierThreshold = nextTierDef ? tierMinThreshold(nextTierDef.subLabel) : 0;
  const queueThresholdPercent = selectedTierBenefit?.queueThresholdPercent ?? 95;
  const queueThresholdVolume = (nextTierThreshold * queueThresholdPercent) / 100;

  const updateQueueThresholdPercent = (value: number) => {
    setTiers((prev) => prev.map((t) => (t.tierKey === selectedTierKey ? { ...t, queueThresholdPercent: value } : t)));
  };

  const updatePreventAutoDemote = (value: boolean) => {
    setTiers((prev) => prev.map((t) => (t.tierKey === selectedTierKey ? { ...t, preventAutoDemote: value } : t)));
  };

  const updatePercent = (value: number) => {
    setTiers((prev) => prev.map((t) => (t.tierKey === selectedTierKey ? { ...t, percent: value } : t)));
  };

  const addBenefit = () => {
    setTiers((prev) =>
      prev.map((t) =>
        t.tierKey === selectedTierKey ? { ...t, benefits: [...t.benefits, { title: "", description: "" }] } : t,
      ),
    );
  };

  const updateBenefit = (idx: number, patch: Partial<TierBenefitItem>) => {
    setTiers((prev) =>
      prev.map((t) =>
        t.tierKey === selectedTierKey
          ? { ...t, benefits: t.benefits.map((b, i) => (i === idx ? { ...b, ...patch } : b)) }
          : t,
      ),
    );
  };

  const updateTitleDescription = (value: any): void => {
    setTiers((prev) =>
      prev.map((t) => (t.tierKey === selectedTierKey ? { ...t, titleDescription: value ?? "" } : t)),
    );
  };

  const updateTierTitle = (value: any): void => {
    setTiers((prev) =>
      prev.map((t) => (t.tierKey === selectedTierKey ? { ...t, tierTitle: value ?? "" } : t)),
    );
  };

  const removeBenefit = (idx: number) => {
    setTiers((prev) =>
      prev.map((t) => (t.tierKey === selectedTierKey ? { ...t, benefits: t.benefits.filter((_, i) => i !== idx) } : t)),
    );
  };

  const isCopyFromYear = mode === "create" && startFrom === "copy";
  const isNewFromScratch = mode === "create" && startFrom !== "copy";
  const totalTierCount = config.tiers.length;
  const filledTierCount = tiers.filter(
    (t) => t.benefits.length > 0 && t.benefits.every((b) => b.title.trim() !== ""),
  ).length;

  let statusText: string;
  if (mode === "edit") {
    statusText = changedTierCount === 0 ? "ยังไม่มีการแก้ไข" : `แก้ไขแล้ว ${changedTierCount} ระดับ`;
  } else if (isCopyFromYear) {
    statusText =
      changedTierCount === 0 ? "ใช้ชุดที่คัดลอกมาทั้งหมด" : `ปรับจากชุดที่คัดลอกมา ${changedTierCount} ระดับ`;
  } else if (isNewFromScratch) {
    statusText =
      filledTierCount === 0 ? "ยังไม่ได้กรอกสิทธิประโยชน์" : `กรอกแล้ว ${filledTierCount} ระดับจาก ${totalTierCount}`;
  } else {
    statusText = changedTierCount === 0 ? "ยังไม่มีการแก้ไข" : "มีการแก้ไขที่ยังไม่บันทึก";
  }

  const beforeUploadBanner = createImageMaxSizeValidator(900, 883);
  const beforeUploadCardImage = createImageMaxSizeValidator(720, 405);
  const beforeUploadBackground = createImageMaxSizeValidator(853, 711);

  return (
    <Form form={form} layout="vertical" initialValues={{ cards: DEFAULT_CARDS, faqs: DEFAULT_FAQS, rules: DEFAULT_RULES }}>
      <div style={{ display: "flex", flexDirection: "column", marginTop: 24 }}>
        <div style={{ marginBottom: 24, background: color.background1, borderRadius: 8, padding: 16 }}>
          <Text fontFamily="Sarabun" fontWeight={600} fontSize={16} color="Text2">
            แบนเนอร์หน้าข้อมูลระดับ
          </Text>
          <div style={{ marginTop: 2, marginBottom: 12 }}>
            <Text fontFamily="Sarabun" fontSize={13} color="Text3">
              ภาพหัวข้อที่ร้านเห็นเมื่อกดดูข้อมูลระดับ ใช้ร่วมกันทุกระดับในปีนี้
            </Text>
          </div>
          <div style={{ display: "flex", alignItems: "start", gap: 16 }}>
            <div
              style={{
                width: 140,
                height: 140,
                borderRadius: 12,
                overflow: "hidden",
                border: `1px solid ${color.border}`,
                background: `linear-gradient(135deg, ${color.Text2} 0%, ${color.secondary} 140%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {bannerUrl ? (
                <img src={bannerUrl} alt="แบนเนอร์" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <img src={image.tierBanner} alt="แบนเนอร์" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "flex-start"}}>
              <ImgCrop aspect={900 / 883} showGrid modalTitle="ปรับขนาดแบนเนอร์" modalOk="ยืนยัน" modalCancel="ยกเลิก">
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={beforeUploadBanner}
                  customRequest={({ file, onSuccess }) => {
                    setBannerUrl(URL.createObjectURL(file as File));
                    onSuccess?.("ok");
                  }}
                >
                  <Button typeButton="border-primary" title="เปลี่ยนแบนเนอร์" style={{ width: "auto" }} />
                </Upload>
              </ImgCrop>
              <div style={{ marginTop: 8 }}>
                <Text fontFamily="Sarabun" fontSize={12} color="Text3">
                  ไฟล์ภาพ PNG ขนาดแนะนำ 900x883 พิกเซล
                </Text>
              </div>
            </div>
          </div>
        </div>

        <CardSection
          style={{ background: color.background1 }}
          leftComponent={
            <SectionHeader title="วิธีการทำงาน" subtitle="การ์ดอธิบายวิธีสะสมยอดและการเลื่อนระดับ ร้านเห็นในแท็บวิธีการทำงาน · ไฟล์ไอคอน: PNG พื้นหลังโปร่งใส ขนาด 144x144 พิกเซล" />
          }
          extra={
            <CountToggle
              count={cardsWatch.length}
              unit="การ์ด"
              expanded={cardsExpanded}
              onToggle={() => setCardsExpanded((v) => !v)}
            />
          }
        >
          {cardsExpanded && (
            <div style={{ padding: "0 16px 16px",background: color.background1 }}>
              <Form.List name="cards">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <ItemRow key={key} onDelete={() => remove(name)}>
                        <div
                          style={{
                            border: `1px solid ${color.border}`,
                            borderRadius: 8,
                            padding: "8px 12px",
                            marginBottom: 6,
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          <Form.Item {...restField} name={[name, "iconUrl"]} noStyle>
                            <IconUploadBox />
                          </Form.Item>
                          <Form.Item {...restField} name={[name, "title"]} noStyle>
                            <Input placeholder="หัวข้อการ์ด" style={{ border: "none", boxShadow: "none", padding: 0, fontWeight: 700 }} />
                          </Form.Item>
                        </div>
                        <Form.Item {...restField} name={[name, "description"]} noStyle>
                          <TextArea placeholder="อธิบายวิธีการทำงาน" rows={2} />
                        </Form.Item>
                      </ItemRow>
                    ))}
                    <Button
                      typeButton="border-dashed-primary"
                      icon={<PlusOutlined />}
                      title="เพิ่มการ์ด"
                      style={{ width: "auto" }}
                      onClick={() => add({ title: "", description: "" })}
                    />
                  </>
                )}
              </Form.List>
            </div>
          )}
        </CardSection>

        <CardSection
          style={{ background: color.background1 }}
          leftComponent={
            <SectionHeader title="คำถามที่พบบ่อย" subtitle="ชุดคำถามของปีนี้ ร้านเห็นในแท็บคำถามที่พบบ่อยของหน้าข้อมูลระดับ" />
          }
          extra={
            <CountToggle
              count={faqsWatch.length}
              unit="คำถาม"
              expanded={faqsExpanded}
              onToggle={() => setFaqsExpanded((v) => !v)}
            />
          }
        >
          {faqsExpanded && (
            <div style={{ padding: "0 16px 16px", background: color.background1 }}>
              <Form.List name="faqs">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <ItemRow key={key} onDelete={() => remove(name)}>
                        <Form.Item {...restField} name={[name, "question"]} noStyle>
                          <Input placeholder="คำถาม" style={{ marginBottom: 6 }} />
                        </Form.Item>
                        <Form.Item {...restField} name={[name, "answer"]} noStyle>
                          <TextArea placeholder="คำตอบ" rows={2} />
                        </Form.Item>
                      </ItemRow>
                    ))}
                    <Button
                      typeButton="border-dashed-primary"
                      icon={<PlusOutlined />}
                      title="เพิ่มคำถาม"
                      style={{ width: "auto" }}
                      onClick={() => add({ question: "", answer: "" })}
                    />
                  </>
                )}
              </Form.List>
            </div>
          )}
        </CardSection>

        <CardSection
          style={{ background: color.background1 }}
          leftComponent={<SectionHeader title="กติกาและเงื่อนไข" subtitle="หัวข้อและเนื้อหาของหน้ากติกาและเงื่อนไขในปีนี้" />}
          extra={
            <CountToggle
              count={rulesWatch.length}
              unit="หัวข้อ"
              expanded={rulesExpanded}
              onToggle={() => setRulesExpanded((v) => !v)}
            />
          }
        >
          {rulesExpanded && (
            <div style={{ padding: "0 16px 16px",background: color.background1 }}>
              <Form.List name="rules">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <div key={key} style={{ marginBottom: 16 }}>
                        <Row wrap={false} align="middle" gutter={8} style={{ marginBottom: 8 }}>
                          <Col flex="auto">
                            <Form.Item {...restField} name={[name, "title"]} noStyle>
                              <Input placeholder="หัวข้อ" />
                            </Form.Item>
                          </Col>
                          <Col flex="none">
                            <div
                              onClick={() => remove(name)}
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: 8,
                                border: `1px solid ${color.error}`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                              }}
                            >
                              <DeleteOutlined style={{ color: color.error }} />
                            </div>
                          </Col>
                        </Row>
                        <QuillContainer>
                          <Form.Item {...restField} name={[name, "content"]} noStyle>
                            <ReactQuill theme="snow" modules={quillModules} style={{
                              background: color.white,
                            }}/>
                          </Form.Item>
                        </QuillContainer>
                      </div>
                    ))}
                    <Button
                      typeButton="border-dashed-primary"
                      icon={<PlusOutlined />}
                      title="เพิ่มหัวข้อ"
                      style={{ width: "auto" }}
                      onClick={() => add({ title: "", content: "" })}
                    />
                  </>
                )}
              </Form.List>
            </div>
          )}
        </CardSection>

        <Text fontWeight={700} fontSize={16} color="Text2">
          รายละเอียดแต่ละระดับ
        </Text>
        <div style={{ display: "flex", gap: 24, marginTop: 12, marginBottom: 8 }}>
          <div style={{ width: 200, flexShrink: 0 }}>
            {config.tiers.map((t) => {
              const active = t.key === selectedTierKey;
              const tierBenefits = tiers.find((x) => x.tierKey === t.key)?.benefits ?? [];
              const hasContent = tierBenefits.length > 0 && tierBenefits.every((b) => b.title.trim() !== "");
              return (
                <div
                  key={t.key}
                  onClick={() => setSelectedTierKey(t.key)}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 8,
                    cursor: "pointer",
                    marginBottom: 4,
                    background: active ? color.background1 : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <Text fontFamily="Sarabun" fontSize={16} fontWeight={active ? 700 : 400} color={active ? "primary" : "Text2"}>
                    {t.label}
                  </Text>
                  {hasContent && (
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: color.softWarning, flexShrink: 0 }} />
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <Text level={3} fontWeight={700} color="Text2">{selectedTierDef?.label}</Text>
            <div style={{ marginTop: 4, marginBottom: 20 }}>
              <Text fontSize={14} color="Text3">เกณฑ์ {selectedTierDef?.subLabel}</Text>
            </div>

            <Text fontWeight={700} fontSize={16}>รายการสิทธิประโยชน์</Text>
            <div style={{ marginTop: 12, marginBottom: 24 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  padding: "8px 0px",
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    background: "#FFF",
                    color: color.background4,
                    border: `1px solid ${color.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <PercentageOutlined />
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
                  <Input
                    value={selectedTierBenefit?.tierTitle ?? ""}
                    onChange={(e) => updateTierTitle(e.target.value)}
                    placeholder="ชื่อสิทธิประโยชน์"
                  />
                  <Input
                    value={selectedTierBenefit?.titleDescription ?? ""}
                    onChange={(e) => updateTitleDescription(e.target.value)}
                    placeholder="คำอธิบายสิทธิประโยชน์"
                  />
                  <Input
                    type="number"
                    value={selectedTierBenefit?.percent ?? 0}
                    onChange={(e) => updatePercent(Number(e.target.value))}
                    suffix="%"
                  />
                </div>
                <div style={{ width : 44 }} />
              </div>

              {selectedTierBenefit?.benefits.map((b, idx) => (
                <div key={idx} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
                  <IconUploadBox value={b.iconUrl} onChange={(v) => updateBenefit(idx, { iconUrl: v })} />
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                    <Input
                      value={b.title}
                      onChange={(e) => updateBenefit(idx, { title: e.target.value })}
                      placeholder="ชื่อสิทธิประโยชน์"
                    />
                    <Input
                      value={b.description}
                      onChange={(e) => updateBenefit(idx, { description: e.target.value })}
                      placeholder="อธิบายสิทธิประโยชน์"
                    />
                  </div>
                  <div
                    onClick={() => removeBenefit(idx)}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      border: `1px solid ${color.error}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    <img src={icon.delete} style={{ width: 15, height: 16 }} alt="Delete" />
                  </div>
                </div>
              ))}

              <Button
                typeButton="border-dashed-primary"
                icon={<PlusOutlined />}
                title="เพิ่มสิทธิประโยชน์"
                style={{ width: "auto" }}
                onClick={addBenefit}
              />
              <div style={{ marginTop: 8 }}>
                <Text fontSize={12} color="Text3">ไฟล์ไอคอน: PNG พื้นหลังโปร่งใส ขนาด 144x144 พิกเซล</Text>
              </div>
            </div>

            <Text fontWeight={700} fontSize={16}>รูปการ์ดระดับ</Text>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start", marginTop: 8, marginBottom: 24 }}>
              <TierCardPreview tier={selectedTierDef} imageOverride={cardImageOverrides[selectedTierKey]} width={180} />
              <div>
                <ImgCrop aspect={720 / 405} showGrid modalTitle="ปรับขนาดรูปการ์ดระดับ" modalOk="ยืนยัน" modalCancel="ยกเลิก">
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={beforeUploadCardImage}
                    customRequest={({ file, onSuccess }) => {
                      setCardImageOverrides((prev) => ({ ...prev, [selectedTierKey]: URL.createObjectURL(file as File) }));
                      onSuccess?.("ok");
                    }}
                  >
                    <Button typeButton="border-primary" title="เปลี่ยนรูป" style={{ width: "auto" }} />
                  </Upload>
                </ImgCrop>
                <div style={{ marginTop: 8 }}>
                  <Text fontSize={12} color="Text3">ไฟล์ภาพสัดส่วน 16:9 ขนาดแนะนำ 720x405 พิกเซล</Text>
                </div>
              </div>
            </div>

            <Text fontWeight={700} fontSize={16}>พื้นหลังของระดับ</Text>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start", marginTop: 8, marginBottom: 24 }}>
              <div
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: 12,
                  border: `1px solid ${color.border}`,
                  overflow: "hidden",
                  background: color.background1,
                  flexShrink: 0,
                }}
              >
                {cardBackgroundOverrides[selectedTierKey] && (
                  <img
                    src={cardBackgroundOverrides[selectedTierKey]}
                    alt="พื้นหลังของระดับ"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                )}
              </div>
              <div>
                <ImgCrop aspect={853 / 711} showGrid modalTitle="ปรับขนาดพื้นหลังของระดับ" modalOk="ยืนยัน" modalCancel="ยกเลิก">
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={beforeUploadBackground}
                    customRequest={({ file, onSuccess }) => {
                      setCardBackgroundOverrides((prev) => ({ ...prev, [selectedTierKey]: URL.createObjectURL(file as File) }));
                      onSuccess?.("ok");
                    }}
                  >
                    <Button typeButton="border-primary" title="เปลี่ยนพื้นหลัง" style={{ width: "auto" }} />
                  </Upload>
                </ImgCrop>
                <div style={{ marginTop: 8 }}>
                  <Text fontSize={12} color="Text3">ไฟล์ภาพแนวตั้ง ขนาดแนะนำ 853x711 พิกเซล</Text>
                </div>
              </div>
            </div>

            <Text fontWeight={700} fontSize={16}>สีของระดับ</Text>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginTop: 8, marginBottom: 24 }}>
              <ColorPicker
                value={selectedColor}
                size='large'
                defaultFormat="hex"
                showText
                onChange={(value) => setColorOverrides((prev) => ({ ...prev, [selectedTierKey]: value.toHexString() }))}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: selectedColor,
                    border: `1px solid ${color.border}`,
                    flexShrink: 0,
                    marginTop: 2,
                    cursor: "pointer",
                  }}
                />
              </ColorPicker>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Text fontWeight={700} fontSize={14} color="Text2">{selectedColor.toUpperCase()}</Text>
                </div>
                {isColorChanged && (
                    <div
                      style={{ width: "auto", padding: "10px 0px", cursor: "pointer" }}
                      onClick={resetColor}
                    >
                      <Text fontWeight={500} fontSize={14} color="primary">คืนค่าสีเดิม</Text>
                    </div>
                  )}
                <div style={{ marginTop: 4 }}>
                  <Text fontSize={12} color="Text3">
                    ใช้กับทุกที่ที่แสดงระดับนี้ ทั้งป้ายในตาราง การ์ดนับร้าน และในแอปร้านค้า
                  </Text>
                </div>
                {hexSaturation(selectedColor) < MIN_TIER_COLOR_SATURATION && (
                  <div style={{ marginTop: 4 }}>
                    <Text fontFamily="Sarabun" fontSize={14} color="error">
                      สีกลืนกับพื้นหลัง
                    </Text>
                  </div>
                )}
              </div>
            </div>
            {
              (selectedTierKey !== "Follow" && selectedTierKey !== "Basic") && (<>
                <Text fontWeight={700} fontSize={16}>เงื่อนไขการพิจารณาระดับ</Text>
                <div style={{ border: `1px solid ${color.border}`, borderRadius: 8, padding: 16, marginTop: 8 }}>
                  {nextTierDef ? (
                    <>
                      <Text fontWeight={700} fontSize={14} color="Text2">เกณฑ์เข้าคิวให้ผู้จัดการพิจารณาเลื่อนขั้น</Text>
                      <div style={{ marginTop: 2, marginBottom: 12 }}>
                        <Text fontSize={13} color="Text3">
                          ร้านที่ทำยอดได้ตาม % ของเกณฑ์ระดับ {nextTierDef.label} จะเข้าคิวพิจารณาเลื่อนขั้น ให้ผู้จัดการตัดสินในรอบรีวิวสิ้นปี
                        </Text>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                        <Input
                          type="number"
                          value={queueThresholdPercent}
                          onChange={(e) => updateQueueThresholdPercent(Number(e.target.value))}
                          suffix="%"
                          style={{ width: 160 }}
                        />
                        <Text fontSize={13} color="Text3">
                          = {formatVolumeAmount(queueThresholdVolume, config.volumeUnit)} {config.volumeUnit} จากเกณฑ์เต็ม {formatVolumeAmount(nextTierThreshold, config.volumeUnit)} {config.volumeUnit}
                        </Text>
                      </div>
                      { config.company != "ICPL" && <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                        <Switch checked={selectedTierBenefit?.preventAutoDemote ?? false} onChange={updatePreventAutoDemote} />
                        <div>
                          <Text fontWeight={700} fontSize={14} color="Text2">ห้ามระบบลดระดับนี้อัตโนมัติ</Text>
                          <div style={{ marginTop: 2 }}>
                            <Text fontSize={12} color="Text3">
                              สิ้นปีร้านที่ยอดไม่ถึงจะเข้าคิวให้ผู้จัดการรีวิวก่อน ระบบจะไม่ลดระดับอัตโนมัติ
                            </Text>
                          </div>
                        </div>
                      </div>}
                    </>
                  ) : (
                    <Text fontSize={13} color="Text3">ระดับนี้เป็นระดับสูงสุดแล้ว ไม่มีระดับถัดไปให้พิจารณาเลื่อนขั้น</Text>
                  )}
                </div>
              </>)
            }
          </div>

          <div style={{ width: 280, background: color.background1, borderRadius: 8, padding: 16, alignSelf: "flex-start", height: "fit-content" }}>
            <div style={{ flexShrink: 0, borderRadius: 8, padding: 16, background: color.white, display: "flex", flexDirection: "column", gap: 8 }}>
              <Text fontSize={13} color="Text3">ตัวอย่างที่ร้านค้าเห็น</Text>
              <div style={{ marginTop: 8 }}>
                <TierCardPreview tier={selectedTierDef} imageOverride={cardImageOverrides[selectedTierKey]} width="100%" />
              </div>
              <div style={{ marginTop: 12 }}>
                <Text fontWeight={700} fontSize={14} color="Text2">สิทธิประโยชน์ของระดับ {selectedTierDef?.label}</Text>
              </div>
              <Tag style={{ borderRadius: "16px", width: "fit-content" }} color={selectedColor}>
                <Text fontFamily="Sarabun" fontSize={14} fontWeight={700} color="white">
                  {selectedTierDef?.label || ""}
                </Text>
              </Tag>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, borderWidth: 1, borderStyle: "solid", borderColor: color.border, borderRadius: 8, padding: "8px 12px" }}>
                <div>
                  <Text fontWeight={700} fontSize={13} color="Text2">{selectedTierBenefit?.tierTitle }</Text>
                  <div>
                    <Text fontSize={12} color="Text3">{selectedTierBenefit?.titleDescription}</Text>
                  </div>
                </div>
                <Text fontSize={20} fontWeight={700} color="secondary3">{selectedTierBenefit?.percent + "%"}</Text>
              </div>
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                {selectedTierBenefit && selectedTierBenefit.benefits.length > 0 ? (
                  selectedTierBenefit.benefits.filter((item) => item.title.trim() !== "").map((b, idx) => (
                    <div key={idx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, borderWidth: 1, borderStyle: "solid", borderColor: color.border, borderRadius: 8, padding: "8px 12px" }}>
                      <div>
                        <Text fontWeight={700} fontSize={13} color="Text2">{b.title || "สิทธิประโยชน์"}</Text>
                        {b.description && (
                          <div>
                            <Text fontSize={12} color="Text3">{b.description}</Text>
                          </div>
                        )}
                      </div>
                      {b.iconUrl && (
                        <img src={b.iconUrl} alt="" style={{ width: 30, height: 30, objectFit: "contain", flexShrink: 0 }} />
                      )}
                    </div>
                  ))
                ) : (
                  <Text fontSize={13} color="Text3">ยังไม่มีสิทธิประโยชน์ในระดับนี้</Text>
                )}
              </div>
            </div>
          </div>
        </div>

        <Divider />
        <Row justify="space-between" align="middle">
          <Col>
            <Button typeButton="border-primary" title="ย้อนกลับ" style={{ width: "auto" }} onClick={onBack} />
          </Col>
          <Col>
            <Row align="middle" gutter={12}>
              <Col>
                <Text fontSize={13} color="Text3">{statusText}</Text>
              </Col>
              <Col>
                <Button
                  typeButton="primary"
                  title="ถัดไป: ตรวจสอบ"
                  style={{ width: "auto" }}
                  onClick={() =>
                    onNext({
                      bannerUrl,
                      cards: form.getFieldValue("cards") || DEFAULT_CARDS,
                      faqs: form.getFieldValue("faqs") || DEFAULT_FAQS,
                      rules: form.getFieldValue("rules") || DEFAULT_RULES,
                      tiers,
                      cardImageOverrides,
                      cardBackgroundOverrides,
                      colorOverrides,
                      changedTierKeys: Array.from(changedTierKeys),
                    })
                  }
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </Form>
  );
}

export default Step2;
